import * as React from 'react';
import { connect } from 'react-redux';

import '../Main.scss';
import '../ImportCustomerFinancials/NewConnectionModal.scss';
import './DestinationStep.scss';
import './RequestStep.scss';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faFileSpreadsheet, faSpinnerThird } from '@awesome.me/kit-8a0d2fd588/icons/modules/classic/light';

import { Button } from '../design-system/components';

import { PortalTextCommon } from '../../Models/Api/strongbox.financialportal';
import { ApplicationState } from '../../Store';
import {
    FieldGuideEngagementSelection,
    FieldGuideMappedRequest,
    FieldGuidePbcRequestList,
    GetFieldGuideMappedWorkbookCount,
    GetFieldGuidePbcRequests,
    GetFieldGuideRequestMappings,
    GetFieldGuideSelectedEngagement,
    RequestMappings,
    actionCreators as FieldGuideStoreActions,
} from '../../Store/FieldGuide';
import { GetPortalText } from '../../Store/PortalAssets';
import { FieldGuideMaxFileNameLength } from '../../Utils/FieldGuideFileName';
import { StringFormat } from '../../Utils/StringUtils';
import { WorkbookRow } from '../FileGroup/WorkbooksCard';

import { RequestPicker } from './RequestPicker';
import { WorkbookNameField } from './WorkbookNameField';

type InjectedReduxState = {
    selectedEngagement: FieldGuideEngagementSelection | undefined;
    commonText: PortalTextCommon | undefined;
    pbcRequests: FieldGuidePbcRequestList | undefined;
    requestMappings: RequestMappings;
    mappedWorkbookCount: number;
};

type InjectedActionCreators = {
    LoadFieldGuidePbcRequests: () => void;
    SetRequestMapping: (variantId: string, request: FieldGuideMappedRequest | undefined, fileName: string) => void;
    NextUploadStep: () => void;
    PreviousUploadStep: () => void;
};

type RequestStepProps = {
    workbooks: WorkbookRow[];
    onCancel: () => void;
};

type Props = RequestStepProps & InjectedReduxState & InjectedActionCreators;

const RequestStepComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        workbooks, onCancel, selectedEngagement, commonText, pbcRequests, requestMappings, mappedWorkbookCount,
        LoadFieldGuidePbcRequests, SetRequestMapping, NextUploadStep, PreviousUploadStep,
    } = props;

    React.useEffect(() => {
        LoadFieldGuidePbcRequests();
        // Mount-only: the thunk loads once per engagement.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [invalidRows, setInvalidRows] = React.useState<ReadonlySet<string>>(new Set<string>());
    const setRowInvalid = React.useCallback((variantId: string, invalid: boolean): void => {
        setInvalidRows(previous => {
            if (previous.has(variantId) === invalid) {
                return previous;
            }

            const next = new Set(previous);
            if (invalid) {
                next.add(variantId);
            } else {
                next.delete(variantId);
            }
            return next;
        });
    }, []);

    const engagementName = selectedEngagement ? selectedEngagement.name : '';
    const status = pbcRequests?.status ?? 'loading';
    const requests = pbcRequests?.status === 'loaded' ? pbcRequests.requests : [];
    const loading = status === 'loading';
    const failed = pbcRequests?.status === 'failed';
    const empty = status === 'loaded' && requests.length === 0;

    const nameLabels = {
        rename: (fileName: string): string => StringFormat(commonText?.fieldGuideRenameFile || 'Rename {0}', fileName),
        reset: commonText?.fieldGuideResetName || 'Reset name',
        limitReached: StringFormat(commonText?.fieldGuideNameLimitReached || '{0} character limit reached', FieldGuideMaxFileNameLength),
        required: commonText?.fieldGuideNameRequired || 'Enter a file name.',
    };

    const renderIntro = (): React.ReactNode => {
        const template = commonText?.fieldGuideSelectRequestIntro || 'Map each workbook to a request in {0}. {1} of {2} workbooks mapped.';
        const [before, ...rest] = template.split('{0}');
        const fill = (part: string): string => StringFormat(part, undefined, mappedWorkbookCount, workbooks.length);
        return (
            <p className={'page-body-text'}>
                {fill(before)}<strong>{engagementName}</strong>{fill(rest.join('{0}'))}
            </p>
        );
    };

    const renderRow = (workbook: WorkbookRow): React.ReactNode => {
        const mapping = requestMappings[workbook.variantId];
        const fileName = mapping && mapping.fileName ? mapping.fileName : workbook.filename;
        const request = mapping ? mapping.request : undefined;

        return (
            <div className={'page-mapping-row'} key={workbook.variantId}>
                <div className={'page-mapping-row__source'}>
                    <FontAwesomeIcon icon={faFileSpreadsheet} className={'page-mapping-row__source-icon'} />
                    <WorkbookNameField
                        originalName={workbook.filename}
                        name={fileName}
                        labels={nameLabels}
                        onCommit={name => SetRequestMapping(workbook.variantId, request, name)}
                        onInvalidChange={invalid => setRowInvalid(workbook.variantId, invalid)}
                    />
                </div>
                <FontAwesomeIcon icon={faArrowRight} className={'page-mapping-row__connector'} />
                <div className={'page-mapping-row__target'}>
                    <RequestPicker
                        requests={requests}
                        selected={request}
                        disabled={loading}
                        placeholder={commonText?.fieldGuideSelectRequestPlaceholder || 'Select request...'}
                        clearLabel={commonText?.fieldGuideClearSelection || 'Clear selection'}
                        onSelect={chosen => SetRequestMapping(workbook.variantId, chosen, fileName)}
                    />
                </div>
            </div>
        );
    };

    const renderBody = (): React.ReactNode => {
        if (failed) {
            return <p className={'page-body-text'} role={'alert'}>{pbcRequests.message || commonText?.fieldGuideLoadRequestsFailed || 'We could not load your Fieldguide requests. Please try again, and contact support if the problem continues.'}</p>;
        }

        if (empty) {
            return <p className={'page-body-text'}>{StringFormat(commonText?.fieldGuideNoRequests || 'No requests on {0}', engagementName)}</p>;
        }

        return (
            <>
                {loading && (
                    <div className={'page-centered-content'} role={'status'}>
                        <FontAwesomeIcon icon={faSpinnerThird} spin className={'page-large-icon'} />
                        <span className={'page-body-text'}>{commonText?.fieldGuideLoadingRequests || 'Loading requests'}</span>
                    </div>
                )}
                <div className={'page-mapping-list'}>
                    {workbooks.map(renderRow)}
                </div>
            </>
        );
    };

    const canReview = !loading && !failed && !empty && mappedWorkbookCount > 0 && invalidRows.size === 0;

    return (
        <div className={'field-guide-step'}>
            <div className={'field-guide-step__content page-stack'}>
                {renderIntro()}
                {renderBody()}
            </div>
            <div className={'field-guide-footer'}>
                <Button type={'floating'} onClick={onCancel}>{commonText?.cancel || 'Cancel'}</Button>
                <div className={'cr-modal-footer__button-group'}>
                    <Button type={'secondary'} className={'page-btn--min-width'} onClick={PreviousUploadStep}>{commonText?.backButton || 'Back'}</Button>
                    <Button type={'primary'} className={'page-btn--min-width'} disabled={!canReview} onClick={NextUploadStep}>
                        {commonText?.fieldGuideReviewButton || 'Review'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export const RequestStepContainer = connect<InjectedReduxState, InjectedActionCreators, RequestStepProps, ApplicationState>(
    (appState: ApplicationState, props: RequestStepProps) => {
        const result: InjectedReduxState = {
            selectedEngagement: GetFieldGuideSelectedEngagement(appState),
            commonText: GetPortalText<PortalTextCommon>(appState, 'commonText'),
            pbcRequests: GetFieldGuidePbcRequests(appState),
            requestMappings: GetFieldGuideRequestMappings(appState),
            mappedWorkbookCount: GetFieldGuideMappedWorkbookCount(appState, props.workbooks.map(workbook => workbook.variantId)),
        };
        return result;
    },
    {
        LoadFieldGuidePbcRequests: () => FieldGuideStoreActions.LoadFieldGuidePbcRequests(),
        SetRequestMapping: FieldGuideStoreActions.SetRequestMapping,
        NextUploadStep: FieldGuideStoreActions.NextUploadStep,
        PreviousUploadStep: FieldGuideStoreActions.PreviousUploadStep,
    }
)(RequestStepComponent);
